import React, { useState, useEffect } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import SeasonalBreakdown from "@/components/SeasonalBreakdown";
import CashFlowSummary from "@/components/CashFlowSummary";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Calculator as CalcIcon, Home, MapPin } from "lucide-react";
import {
  MARKET_PRESETS,
  NOVA_SEASONAL_WEIGHTS,
  type MarketPreset,
} from "@/lib/marketPresets";
import { computeCalcResults } from "@/lib/calcEngine";

export type { MarketPreset };
export { MARKET_PRESETS, NOVA_SEASONAL_WEIGHTS };

const CalculatorPage = () => {
  const [selectedMarketId, setSelectedMarketId] =
    useState<string>("alexandria");
  const [avgNightlyRate, setAvgNightlyRate] = useState<number>(500);
  const [targetOccupancy, setTargetOccupancy] = useState<number>(83);
  const [annualRentalIncome, setAnnualRentalIncome] = useState<number>(151475);
  const [annualOtherIncome, setAnnualOtherIncome] = useState<number>(0);
  const [airbnbPlatformFeeRate, setAirbnbPlatformFeeRate] =
    useState<number>(0.155);
  const [mortgage, setMortgage] = useState<number>(6000);
  const [utilities, setUtilities] = useState<number>(350);
  const [managementFeeRate, setManagementFeeRate] = useState<number>(0.2);
  const [maintenance, setMaintenance] = useState<number>(100);
  const [supplies, setSupplies] = useState<number>(50);
  const [otherFees, setOtherFees] = useState<number>(50);

  const [managementOnly, setManagementOnly] = useState<boolean>(false);
  const [purchasePrice, setPurchasePrice] = useState<number>(900000);
  const [downPaymentPercent, setDownPaymentPercent] = useState<number>(20);
  const [closingCostsPercent, setClosingCostsPercent] = useState<number>(3);
  const [furnishingCost, setFurnishingCost] = useState<number>(25000);
  const [interestRate, setInterestRate] = useState<number>(7.25);
  const [loanTerm, setLoanTerm] = useState<number>(30);
  const [propertyTaxRate, setPropertyTaxRate] = useState<number>(1);
  const [annualInsurance, setAnnualInsurance] = useState<number>(1600);

  const handleRateOrOccupancyChange = (
    newRate: number,
    newOccupancy: number,
  ) => {
    setAvgNightlyRate(newRate);
    setTargetOccupancy(newOccupancy);
    const calculatedAnnual = Math.round(newRate * 365 * (newOccupancy / 100));
    setAnnualRentalIncome(calculatedAnnual);
  };

  const handleMarketChange = (marketId: string) => {
    const market = MARKET_PRESETS.find((m) => m.id === marketId);
    if (market) {
      setSelectedMarketId(marketId);
      setAvgNightlyRate(market.avgNightlyRate);
      setTargetOccupancy(market.targetOccupancy);
      setAnnualRentalIncome(market.annualRentalIncome);
      setPurchasePrice(market.purchasePrice);
      setDownPaymentPercent(market.downPaymentPercent);
      setClosingCostsPercent(market.closingCostsPercent);
      setFurnishingCost(market.furnishingCost);
      setInterestRate(market.interestRate);
      setLoanTerm(market.loanTerm);
      setPropertyTaxRate(market.propertyTaxRate);
      setAnnualInsurance(market.annualInsurance);
      setUtilities(market.utilities);
      setMaintenance(market.maintenance);
      setSupplies(market.supplies);
      setOtherFees(market.otherFees);
      setMortgage(market.mortgageEstimate);
    }
  };

  const r = computeCalcResults({
    annualRentalIncome,
    annualOtherIncome,
    airbnbPlatformFeeRate,
    managementFeeRate,
    mortgage,
    utilities,
    maintenance,
    supplies,
    otherFees,
    targetOccupancy,
    purchasePrice,
    downPaymentPercent,
    closingCostsPercent,
    furnishingCost,
    interestRate,
    loanTerm,
    propertyTaxRate,
    annualInsurance,
  });

  useEffect(() => {
    if (!managementOnly) {
      setMortgage(Math.round(r.estimatedMortgage));
    }
  }, [managementOnly, r.estimatedMortgage]);

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Airbnb Cash Flow Calculator | JNN Homes"
        description="Calculate your estimated Airbnb cash flow with JNN Homes. Estimate your rental income, expenses, and management fees to see potential net profit."
        canonical="https://www.jnnhomes.com/calculator"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "JNN Homes Airbnb Cash Flow Calculator",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          url: "https://www.jnnhomes.com/calculator",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
          description:
            "Free Airbnb cash flow calculator for VA & DC short-term rental properties. Estimate annual revenue, expenses, and net cash flow by market.",
        }}
      />
      <Navbar />

      <main className="pt-24 pb-16 min-h-screen">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-10">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
              Cash Flow Calculator
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Estimate your potential cash flow across different VA & DC
              markets.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-10">
            {MARKET_PRESETS.map((market) => (
              <button
                key={market.id}
                onClick={() => handleMarketChange(market.id)}
                className={`flex flex-col items-center justify-center p-4 rounded-xl border-2 transition-all text-left ${
                  selectedMarketId === market.id
                    ? "border-primary bg-primary/5 ring-4 ring-primary/10"
                    : "border-border bg-card hover:border-primary/40"
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MapPin
                    className={`w-4 h-4 ${selectedMarketId === market.id ? "text-primary" : "text-muted-foreground"}`}
                  />
                  <span className="font-bold text-sm">{market.name}</span>
                </div>
                <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-tight">
                  {market.badge}
                </span>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div
                onClick={() => setManagementOnly(!managementOnly)}
                className={`flex items-center justify-between p-5 md:p-6 border-2 rounded-xl transition-all duration-300 cursor-pointer ${
                  managementOnly
                    ? "border-primary bg-primary/5 shadow-md ring-4 ring-primary/10"
                    : "border-border bg-card shadow-sm hover:border-primary/40 hover:shadow-md"
                }`}
              >
                <div className="space-y-1.5 pr-4">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg transition-colors ${managementOnly ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}
                    >
                      <Home className="w-5 h-5" />
                    </div>
                    <Label className="text-base md:text-lg font-bold cursor-pointer pointer-events-none">
                      I already own the property
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground md:pl-12">
                    Turn on to hide acquisition costs if you already own the
                    property.
                  </p>
                </div>
                <div onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={managementOnly}
                    onCheckedChange={setManagementOnly}
                    className="scale-125 data-[state=checked]:bg-primary"
                  />
                </div>
              </div>

              {!managementOnly && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-2xl flex items-center gap-2 text-primary">
                      <Home className="w-6 h-6" /> Acquisition Costs
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="purchasePrice">
                          Purchase Price ($)
                        </Label>
                        <Input
                          id="purchasePrice"
                          type="number"
                          value={purchasePrice || ""}
                          onChange={(e) =>
                            setPurchasePrice(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="downPayment">Down Payment (%)</Label>
                        <Input
                          id="downPayment"
                          type="number"
                          value={downPaymentPercent || ""}
                          onChange={(e) =>
                            setDownPaymentPercent(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="closingCosts">Closing Costs (%)</Label>
                        <Input
                          id="closingCosts"
                          type="number"
                          value={closingCostsPercent || ""}
                          onChange={(e) =>
                            setClosingCostsPercent(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="furnishingCost">
                          Furnishing & Setup ($)
                        </Label>
                        <Input
                          id="furnishingCost"
                          type="number"
                          value={furnishingCost || ""}
                          onChange={(e) =>
                            setFurnishingCost(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="interestRate">Interest Rate (%)</Label>
                        <Input
                          id="interestRate"
                          type="number"
                          step="0.1"
                          value={interestRate || ""}
                          onChange={(e) =>
                            setInterestRate(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="loanTerm">Loan Term (Years)</Label>
                        <Input
                          id="loanTerm"
                          type="number"
                          value={loanTerm || ""}
                          onChange={(e) => setLoanTerm(Number(e.target.value))}
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="propertyTax">Property Tax (%)</Label>
                        <Input
                          id="propertyTax"
                          type="number"
                          step="0.1"
                          value={propertyTaxRate || ""}
                          onChange={(e) =>
                            setPropertyTaxRate(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="insurance">Annual Insurance ($)</Label>
                        <Input
                          id="insurance"
                          type="number"
                          value={annualInsurance || ""}
                          onChange={(e) =>
                            setAnnualInsurance(Number(e.target.value))
                          }
                          className="text-lg"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2 text-emerald-600">
                    <DollarSign className="w-6 h-6" /> Annual Revenue & Seasonal
                    Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="bg-muted/40 p-4 rounded-xl border space-y-4">
                    <div className="text-sm font-semibold text-foreground flex items-center justify-between">
                      <span>Annual Revenue Projection Formula</span>
                      <span className="text-xs font-normal text-muted-foreground">
                        365 days / year
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="avgNightlyRate">
                          Average Nightly Rate ($)
                        </Label>
                        <Input
                          id="avgNightlyRate"
                          type="number"
                          value={avgNightlyRate || ""}
                          onChange={(e) =>
                            handleRateOrOccupancyChange(
                              Number(e.target.value),
                              targetOccupancy,
                            )
                          }
                          className="text-lg bg-background"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="targetOccupancy">
                          Target Occupancy (%)
                        </Label>
                        <Input
                          id="targetOccupancy"
                          type="number"
                          value={targetOccupancy || ""}
                          onChange={(e) =>
                            handleRateOrOccupancyChange(
                              avgNightlyRate,
                              Number(e.target.value),
                            )
                          }
                          className="text-lg bg-background"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Formula: ${avgNightlyRate} × 365 days × {targetOccupancy}%
                      ={" "}
                      <strong className="text-foreground">
                        $
                        {Math.round(
                          avgNightlyRate * 365 * (targetOccupancy / 100),
                        ).toLocaleString()}
                      </strong>{" "}
                      / year (~$
                      {Math.round(
                        (avgNightlyRate * 365 * (targetOccupancy / 100)) / 12,
                      ).toLocaleString()}
                      /mo avg)
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="annualRentalIncome">
                        Estimated Annual Rental Revenue ($)
                      </Label>
                      <Input
                        id="annualRentalIncome"
                        type="number"
                        value={annualRentalIncome || ""}
                        onChange={(e) =>
                          setAnnualRentalIncome(Number(e.target.value))
                        }
                        className="text-lg"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="annualOtherIncome">
                        Other Annual Incomes ($)
                      </Label>
                      <Input
                        id="annualOtherIncome"
                        type="number"
                        value={annualOtherIncome || ""}
                        onChange={(e) =>
                          setAnnualOtherIncome(Number(e.target.value))
                        }
                        className="text-lg"
                      />
                    </div>
                  </div>

                  <SeasonalBreakdown breakdown={r.monthlyBreakdown} />

                  <div className="pt-2 border-t space-y-3">
                    <Label className="text-base font-semibold">
                      Airbnb Platform Service Fee
                    </Label>
                    <RadioGroup
                      value={airbnbPlatformFeeRate.toString()}
                      onValueChange={(val) =>
                        setAirbnbPlatformFeeRate(Number(val))
                      }
                      className="grid grid-cols-1 md:grid-cols-2 gap-4"
                    >
                      <div>
                        <RadioGroupItem
                          value="0.155"
                          id="platform-15"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="platform-15"
                          className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-3.5 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center"
                        >
                          <span className="text-lg font-bold">
                            15.5% (Host-Only)
                          </span>
                          <span className="text-xs text-muted-foreground mt-1">
                            Software / Pro manager connected (Default)
                          </span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem
                          value="0.03"
                          id="platform-3"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="platform-3"
                          className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-3.5 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center"
                        >
                          <span className="text-lg font-bold">
                            3% (Split Fee)
                          </span>
                          <span className="text-xs text-muted-foreground mt-1">
                            Standard listing fee
                          </span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl flex items-center gap-2 text-destructive">
                    <CalcIcon className="w-6 h-6" /> Expenses
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <Label className="text-base font-semibold">
                      Co-Hosting Fee Package
                    </Label>
                    <RadioGroup
                      value={managementFeeRate.toString()}
                      onValueChange={(val) => setManagementFeeRate(Number(val))}
                      className="grid grid-cols-1 md:grid-cols-3 gap-4"
                    >
                      {["0.15", "0.2", "0.25"].map((rate) => (
                        <div key={rate}>
                          <RadioGroupItem
                            value={rate}
                            id={`fee-${rate}`}
                            className="peer sr-only"
                          />
                          <Label
                            htmlFor={`fee-${rate}`}
                            className="flex flex-col items-center justify-between rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer"
                          >
                            <span className="text-xl font-bold">
                              {parseFloat(rate) * 100}%
                            </span>
                            <span className="text-sm text-muted-foreground mt-1">
                              {rate === "0.15"
                                ? "Essential"
                                : rate === "0.2"
                                  ? "Full Service"
                                  : "Premium Plus"}
                            </span>
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="mortgage">Mortgage / Rent ($)</Label>
                      <Input
                        id="mortgage"
                        type="number"
                        value={mortgage || ""}
                        onChange={(e) => setMortgage(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="utilities">Utilities ($)</Label>
                      <Input
                        id="utilities"
                        type="number"
                        value={utilities || ""}
                        onChange={(e) => setUtilities(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="maintenance">Maintenance Cost ($)</Label>
                      <Input
                        id="maintenance"
                        type="number"
                        value={maintenance || ""}
                        onChange={(e) => setMaintenance(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="supplies">Supplies ($)</Label>
                      <Input
                        id="supplies"
                        type="number"
                        value={supplies || ""}
                        onChange={(e) => setSupplies(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="otherFees">
                        Other Fees (Yard, Trash, etc.) ($)
                      </Label>
                      <Input
                        id="otherFees"
                        type="number"
                        value={otherFees || ""}
                        onChange={(e) => setOtherFees(Number(e.target.value))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                {/* Annual Cash Flow Summary — numbers blurred until unlocked */}
                <CashFlowSummary
                  r={r}
                  airbnbPlatformFeeRate={airbnbPlatformFeeRate}
                  managementFeeRate={managementFeeRate}
                  furnishingCost={furnishingCost}
                  managementOnly={managementOnly}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CalculatorPage;
